import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShoppingCart, CheckCircle2, Clock, Truck, Package, X, Phone, Mail, ExternalLink } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: "pending",    label: "En attente",   icon: Clock,         desc: "Votre commande a été reçue et est en attente de confirmation." },
  { key: "confirmed",  label: "Confirmée",    icon: CheckCircle2,  desc: "Votre commande a été confirmée. Nous allons procéder au traitement." },
  { key: "processing", label: "En traitement", icon: Package,       desc: "Votre commande est en cours de préparation." },
  { key: "shipped",    label: "Expédiée",     icon: Truck,         desc: "Votre commande est en route !" },
  { key: "delivered",  label: "Livrée",       icon: CheckCircle2,  desc: "Votre commande a été livrée. Merci de votre confiance !" },
];

const STATUS_INDEX = Object.fromEntries(STATUS_STEPS.map((s, i) => [s.key, i]));

export default function PublicOrderPage() {
  const { token } = useParams();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/orders/${token}`)
      .then(res => {
        setData(res.data);
        document.title = `Suivi commande — ${res.data.shop?.shop_name || "AMCPhone"}`;
      })
      .catch(() => setError("Ce lien est invalide ou a expiré."))
      .finally(() => setLoading(false));
    return () => { document.title = "AMCPhone Martinique"; };
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center">
        <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Lien invalide</h2>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  const { order, shop, repair_ticket } = data;
  const currentStep = STATUS_INDEX[order.status] ?? 0;
  const isCancelled = order.status === "cancelled";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-blue-900 px-4 py-5">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-bold text-lg text-white">{shop?.shop_name}</h1>
            <p className="text-sm text-blue-200">{shop?.shop_address}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-blue-300">Commande</p>
            <p className="font-bold text-white text-lg">{order.order_number}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* Statut annulé */}
        {isCancelled && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <X className="w-6 h-6 text-red-500 shrink-0" />
            <div>
              <p className="font-medium text-red-800">Commande annulée</p>
              <p className="text-sm text-red-600">Cette commande a été annulée. Contactez-nous pour plus d'informations.</p>
            </div>
          </div>
        )}

        {/* Timeline statut */}
        {!isCancelled && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h2 className="font-bold text-gray-800 mb-4">Suivi de commande</h2>
            <div className="space-y-3">
              {STATUS_STEPS.map((step, i) => {
                const done    = i <= currentStep;
                const current = i === currentStep;
                const Icon    = step.icon;
                return (
                  <div key={step.key} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                        done ? (current ? "bg-blue-600" : "bg-emerald-500") : "bg-gray-100"
                      }`}>
                        <Icon className={`w-4 h-4 ${done ? "text-white" : "text-gray-400"}`} />
                      </div>
                      {i < STATUS_STEPS.length - 1 && (
                        <div className={`w-0.5 h-6 mt-1 ${i < currentStep ? "bg-emerald-400" : "bg-gray-200"}`} />
                      )}
                    </div>
                    <div className="pb-2">
                      <p className={`font-medium text-sm ${done ? (current ? "text-blue-700" : "text-emerald-700") : "text-gray-400"}`}>
                        {step.label}
                        {current && <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">En cours</span>}
                      </p>
                      {current && <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Détail commande */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-xs text-gray-400 uppercase font-medium mb-1">
                {order.doc_type === "facture" ? "Facture" : order.doc_type === "devis" ? "Devis" : "Commande"}
                {order.doc_number && ` · ${order.doc_number}`}
              </p>
              <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
            {order.doc_date && <p className="text-sm text-gray-500">Date doc : {order.doc_date}</p>}
          </div>

          {/* Articles */}
          <div className="space-y-1 mb-4">
            <div className="grid grid-cols-12 gap-2 text-xs text-gray-400 font-medium uppercase pb-1 border-b border-gray-100">
              <span className="col-span-6">Description</span>
              <span className="col-span-2 text-center">Qté</span>
              <span className="col-span-2 text-right">P.U</span>
              <span className="col-span-2 text-right">Total</span>
            </div>
            {(order.items || []).map((item, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 text-sm py-1.5 border-b border-gray-50">
                <span className="col-span-6 text-gray-700">
                  {item.name}
                  {item.remise > 0 && <span className="text-amber-500 text-xs ml-1">-{item.remise}%</span>}
                </span>
                <span className="col-span-2 text-center text-gray-500">{item.quantity}</span>
                <span className="col-span-2 text-right text-gray-500">{parseFloat(item.unit_price||0).toFixed(2)}€</span>
                <span className="col-span-2 text-right font-medium text-gray-800">{parseFloat(item.total_line||0).toFixed(2)}€</span>
              </div>
            ))}
          </div>

          <div className="flex justify-between font-bold text-base border-t border-gray-200 pt-3">
            <span className="text-gray-800">NET À PAYER</span>
            <span className="text-blue-600 text-lg">{parseFloat(order.total||0).toFixed(2)}€</span>
          </div>
          {order.payment_mode && (
            <p className="text-xs text-gray-400 mt-1">Mode de règlement : {order.payment_mode}</p>
          )}
        </div>

        {/* Lien suivi réparation si lié */}
        {repair_ticket && (
          <a
            href={`https://app.amcphone-martinique.fr/track/${repair_ticket.ticket_number}`}
            className="block bg-slate-800 rounded-2xl p-4 text-white hover:bg-slate-700 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">🔧 Suivre ma réparation</p>
                <p className="text-sm text-slate-300 mt-0.5">Ticket {repair_ticket.ticket_number} · {repair_ticket.status_label || repair_ticket.status}</p>
              </div>
              <ExternalLink className="w-5 h-5 text-slate-400" />
            </div>
          </a>
        )}

        {/* Contact */}
        <div className="text-center text-sm text-gray-400 pb-8 space-y-1">
          <p className="font-medium text-gray-600">{shop?.shop_name}</p>
          {shop?.shop_phone && (
            <p className="flex items-center justify-center gap-1">
              <Phone className="w-3.5 h-3.5" />{shop.shop_phone}
            </p>
          )}
          {shop?.shop_email && (
            <p className="flex items-center justify-center gap-1">
              <Mail className="w-3.5 h-3.5" />{shop.shop_email}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
